import ModalView from "@modals/modalview/ModalView";
import styles from "./ScanToUploadFile.module.css";
import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";

import {
  createAnswersheetSubmission,
  getAnswersheetUploadURL,
  uploadFileToS3
} from "@services/answersheetUpload";
import PDFViewer from "./PDFViewer";

function ScanToUploadFile({
  examSessionId,
  questionPaperId,
  isOpen,
  onClose
}: {
  examSessionId: string;
  questionPaperId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [status, setStatus] = useState("");
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [admissionId, setAdmissionId] = useState("");
  const [hasVideoSignal, setHasVideoSignal] = useState(false);
  const [isAutoCrop, setIsAutoCrop] = useState(false);
  const [isScanningBarcode, setIsScanningBarcode] = useState(false);
  const videoPreviewRef = useRef<HTMLImageElement>(null);
  const latestFrameRef = useRef<string | null>(null);

  const handleToggleAutoCrop = async () => {
    if (!window.electronAPI) return;

    const newMode = !isAutoCrop;
    setIsAutoCrop(newMode);
    try {
      await window.electronAPI.setCutMode(newMode ? 1 : 0);
      setStatus(newMode ? "Auto Crop Enabled" : "Auto Crop Disabled");
    } catch (e) {
      console.error("Failed to set cut mode", e);
      setStatus("Failed to toggle Auto Crop");
      setIsAutoCrop(!newMode);
    }
  };
  const clickSound = new Audio("/shuttersound.mp3");

  useEffect(() => {
    if (!window.electronAPI) return;

    const handleFrame = (base64: string) => {
      latestFrameRef.current = base64;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.src = base64;
      }
      setHasVideoSignal(prev => prev ? prev : true);
    };

    window.electronAPI.onCameraFrame(handleFrame);

    return () => {
      if (window.electronAPI) window.electronAPI.stopScan();
    };
  }, []);

  const handleStartScanning = async () => {
    if (!window.electronAPI) {
      setStatus("Hardware bridge not found");
      return;
    }

    setIsScanning(true);
    setStatus("Initializing Scanner...");

    try {
      await window.electronAPI.connect();
      await window.electronAPI.openCamera(0, 1024, 768);
      setStatus("Scanner Hardware Ready");
    } catch (err) {
      console.error(err);
      setStatus("Failed to start hardware");
    }
  };

  const handleKeepScanning = () => {
    setSelectedImageIndex(null);
    setReplacingIndex(null);
    setStatus("Resumed Live View");
  };

  const handleCapture = async () => {
    if (!window.electronAPI) return;

    if (isScanningBarcode) return;

    clickSound.currentTime = 0;
    clickSound.play().catch(() => { });

    try {
      if (!admissionId) {
        setIsScanningBarcode(true);
        setStatus("Scanning for Barcode...");
        try {
          const barcode = await window.electronAPI.scanBarcode(0);
          if (barcode) {
            setAdmissionId(barcode);
            setStatus(`Barcode Found: ${barcode}`);
          }
        } catch (err) {
          console.warn("Barcode scan error", err);
        }

        await new Promise(r => setTimeout(r, 1500));
      }

      setStatus(replacingIndex !== null ? "Retaking Image..." : "Capturing Image...");
      const capturedImg = await window.electronAPI.capturePhoto();

      setImages(prev => {
        if (replacingIndex !== null) {
          const newImages = [...prev];
          newImages[replacingIndex] = capturedImg;
          return newImages;
        }
        return [...prev, capturedImg];
      });

      if (replacingIndex !== null) {
        setStatus(`Replaced Page ${replacingIndex + 1}`);
        setReplacingIndex(null);
      } else {
        setStatus(`Captured Page ${images.length + 1}`);
      }
    } catch (error) {
      console.error("Capture failed:", error);
      setStatus("Capture Failed");
    } finally {
      setIsScanningBarcode(false);
    }
  };

  const handleDelete = () => {
    if (selectedImageIndex === null) return;
    const idxToRemove = selectedImageIndex;
    setImages(prev => prev.filter((_, i) => i !== idxToRemove));
    setSelectedImageIndex(null);
    setStatus(`Deleted Page ${idxToRemove + 1}`);
  };

  const handleReplace = () => {
    if (selectedImageIndex === null) return;
    setReplacingIndex(selectedImageIndex);
    setSelectedImageIndex(null);
    setStatus(`Ready to recapture Page ${selectedImageIndex + 1}`);
  };

  const handleGeneratePDFAndUpload = async () => {
    try {
      if (images.length === 0) {
        setStatus("No images captured");
        return;
      }

      setStatus("Generating High-Quality PDF...");

      // Helper to load image
      const loadImage = (src: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve(img);
          img.onerror = reject;
        });
      };

      const firstImg = await loadImage(images[0]);
      const width = firstImg.width;
      const height = firstImg.height;
      const orientation: "l" | "p" = width > height ? "l" : "p";

      const pdf = new jsPDF({
        orientation,
        unit: "px",
        format: [width, height],
        compress: true
      });

      pdf.addImage(images[0], "JPEG", 0, 0, width, height, undefined, 'FAST');

      for (let i = 1; i < images.length; i++) {
        const img = await loadImage(images[i]);
        const w = img.width;
        const h = img.height;
        const o = w > h ? "l" : "p";

        pdf.addPage([w, h], o);
        pdf.addImage(images[i], "JPEG", 0, 0, w, h, undefined, 'FAST');
      }

      const pdfBlob = pdf.output("blob");
      const pdfFile = new File([pdfBlob], `${admissionId}.pdf`, { type: "application/pdf" });
      const fileKey = `${admissionId}_${examSessionId}.pdf`;

      const response: any = await getAnswersheetUploadURL({
        filename: fileKey,
      });

      await uploadFileToS3(pdfFile, response.upload_url);

      await createAnswersheetSubmission({
        exam_session_id: examSessionId,
        submitted_by: admissionId,
        question_paper_submissions_documents: [
          {
            question_paper_id: questionPaperId,
            document: {
              filename: fileKey,
            },
          },
        ],
      });

      // const url = URL.createObjectURL(pdfBlob);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = `${admissionId}_${examSessionId}.pdf`;
      // document.body.appendChild(a);
      // a.click();

      // a.remove();
      // URL.revokeObjectURL(url);

      setUploadCompleted(true);
      setStatus("Upload Successful");
    } catch (error) {
      console.error(error);
      setStatus("PDF Generation Failed");
    }
  };

  const handleStartNewScan = () => {
    setImages([]);
    setSelectedImageIndex(null);
    setIsPreviewMode(false);
    setAdmissionId("");
    setUploadCompleted(false);
    setStatus("Ready for next student");
  };

  const handleNext = () => {
    if (!admissionId.trim()) {
      setStatus("Please enter Admission ID");
      return;
    }
    if (images.length === 0) {
      setStatus("Please capture at least one page");
      return;
    }
    setIsPreviewMode(true);
  };

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.scanToUploadContainer}>
        <div className={styles.header}>
          <h2>{isPreviewMode ? "Final Review" : "Answer Sheet Scanner"}</h2>
          <div className={styles.headerButtons}>
            {isPreviewMode && (
              <button onClick={() => setIsPreviewMode(false)} className={styles.backButton}>
                ← Back to Scanner
              </button>
            )}
            <button className={styles.backButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {!isPreviewMode ? (
          <div className={styles.admissionIdSection}>
            <label>Admission ID*</label>
            <input
              value={admissionId}
              onChange={e => setAdmissionId(e.target.value)}
              className={styles.admissionInput}
              placeholder="Enter Student ID"
            />
          </div>
        ) : (
          <div className={styles.previewInfo}>
            <p><strong>Student:</strong> {admissionId}</p>
            <p><strong>Total Pages:</strong> {images.length}</p>
          </div>
        )}

        <div className={styles.mainContent}>
          <div className={styles.leftPanel}>
            {isPreviewMode ? (
              <PDFViewer images={images} />
            ) : (
              <>
                <div className={styles.mainImageContainer}>
                  {selectedImageIndex === null && hasVideoSignal ? (
                    <img
                      ref={videoPreviewRef}
                      src={latestFrameRef.current || ""}
                      className={styles.mainImage}
                      alt="Hardware Preview"
                    />
                  ) : selectedImageIndex !== null && images[selectedImageIndex] ? (
                    <img src={images[selectedImageIndex]} className={styles.mainImage} alt="Captured Page" />
                  ) : (
                    <div className={styles.noImagePlaceholder}>Scanner Not Connected</div>
                  )}
                </div>
                {images.length > 0 && (
                  <div className={styles.thumbnailSection}>
                    <div className={styles.thumbnailRow}>
                      {images.map((img, idx) => (
                        <div
                          key={idx}
                          className={styles.thumbnailWrapper}
                          onClick={() => setSelectedImageIndex(idx)}
                        >
                          <img
                            src={img}
                            className={`${styles.thumbnail} ${idx === selectedImageIndex ? styles.selectedThumbnail : ""}`}
                            alt={`Page ${idx + 1}`}
                          />
                          <span className={styles.thumbnailIndex}>{idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.rightPanel}>
            {!isPreviewMode ? (
              <>
                {selectedImageIndex === null ? (
                  // Live View Actions
                  <>
                    <div className={styles.toggleContainer}>
                      <label className={styles.switch}>
                        <input type="checkbox" checked={isAutoCrop} onChange={handleToggleAutoCrop} disabled={!isScanning} />
                        <span className={styles.slider}></span>
                      </label>
                      <span className={styles.toggleLabel}>Auto Crop</span>
                    </div>

                    <button onClick={handleStartScanning} className={styles.actionButton} disabled={isScanning}>
                      Start Scanner
                    </button>
                    <button onClick={handleCapture} className={styles.actionButton} disabled={isScanningBarcode}>
                      {replacingIndex !== null ? `Recapture Page ${replacingIndex + 1}` : "Capture Page"}
                    </button>
                    {replacingIndex !== null && (
                      <button onClick={() => { setReplacingIndex(null); setStatus("Cancelled Replace"); }} className={styles.actionButton}>
                        Cancel Replace
                      </button>
                    )}
                  </>
                ) : (
                  // Image Preview Actions
                  <>
                    <button onClick={handleKeepScanning} className={styles.actionButton}>
                      Keep Scanning
                    </button>
                    <button onClick={handleReplace} className={styles.actionButton}>
                      Retake Page
                    </button>
                    <button onClick={handleDelete} className={styles.deleteButton || styles.actionButton}>
                      Delete Page
                    </button>
                  </>
                )}
                <button onClick={handleNext} className={styles.actionButton}>
                  Proceed to Upload
                </button>
              </>
            ) : (
              <>
                <button onClick={handleGeneratePDFAndUpload} className={styles.generatePdfButton} disabled={uploadCompleted}>
                  {uploadCompleted ? "Upload Finished" : "Confirm & Upload PDF"}
                </button>
                <button onClick={handleStartNewScan} className={styles.nextButton} disabled={!uploadCompleted}>
                  Scan Next Student
                </button>
              </>
            )}
          </div>
        </div>

        {status && <div key={status} className={styles.statusMessage}>{status}</div>}
      </div>
    </ModalView>
  );
}

export default ScanToUploadFile;